import { CampaignState } from '@ms/yammer-graphql';

import { Campaign, Experiment, YammerState } from '../../state/types';
import { findCampaignReferenceByIdThrows } from '../campaignReferences/selectors';
import { findCampaignById } from '../campaigns/selectors';
import { getExperimentTreatment } from '../experiments/selectors';
import { getFeatureAccessSettings, getFeatureAccessSettingsStatus } from '../featureAccessSettings/selectors';

export enum CampaignAccess {
  Unknown,
  NotAllowed,
  Allowed,
}
type IsCampaignsEnabled = (state: YammerState) => boolean;
export const isCampaignsEnabled: IsCampaignsEnabled = (state) => {
  const campaignsIsEnabled = getExperimentTreatment(state, Experiment.Campaigns) === true;
  const vivaEngagePremiumIsEnabled = getExperimentTreatment(state, Experiment.VivaEngagePremium) === true;
  const campaignsKillSwitchIsEnabled = getExperimentTreatment(state, Experiment.CampaignsKillSwitchUI) === true;

  return !campaignsKillSwitchIsEnabled && (campaignsIsEnabled || vivaEngagePremiumIsEnabled);
};

type IsCampaignAutoSuggestionEnabled = (state: YammerState) => boolean;
export const isCampaignAutoSuggestionEnabled: IsCampaignAutoSuggestionEnabled = (state) => {
  const autoSuggestionEnabled = getExperimentTreatment(state, Experiment.AutoSuggestionCampaignHashtag) === true;

  return autoSuggestionEnabled && isCampaignsEnabled(state);
};

type IsCampaignGoalTrackerEnabled = (state: YammerState) => boolean;
export const isCampaignGoalTrackerEnabled: IsCampaignGoalTrackerEnabled = (state) => isCampaignsEnabled(state);

type IsCampaignsExecutiveSponsorshipEnabled = (state: YammerState) => boolean;
export const isCampaignsExecutiveSponsorshipEnabled: IsCampaignsExecutiveSponsorshipEnabled = (state) => {
  const executiveSponsorshipIsEnabled =
    getExperimentTreatment(state, Experiment.CampaignsExecutiveSponsorship) === true;

  return executiveSponsorshipIsEnabled && isCampaignsEnabled(state);
};

type IsCampaignSashEnabled = (state: YammerState) => boolean;
export const isCampaignSashEnabled: IsCampaignSashEnabled = (state) => {
  const campaignSashIsEnabled = getExperimentTreatment(state, Experiment.CampaignSash) === true;

  return campaignSashIsEnabled && isCampaignsEnabled(state);
};

type IsCampaignAccessSettingsPending = (state: YammerState) => boolean;
export const isCampaignAccessSettingsPending: IsCampaignAccessSettingsPending = (state) =>
  getFeatureAccessSettingsStatus(state) === 'pending';

type CanCreateCampaigns = (state: YammerState) => boolean;
export const canCreateCampaigns: CanCreateCampaigns = (state) =>
  !!getFeatureAccessSettings(state)?.viewerCanCreateCampaigns;

type CanAccessCampaignsDashboard = (state: YammerState) => boolean;
export const canAccessCampaignsDashboard: CanAccessCampaignsDashboard = (state) =>
  !!getFeatureAccessSettings(state)?.viewerCanAccessCampaignsDashboard;

type CanAccessCampaignsRecommendations = (state: YammerState) => boolean;
export const canAccessCampaignsRecommendations: CanAccessCampaignsRecommendations = (state) =>
  !!getFeatureAccessSettings(state)?.viewerCanAccessCampaignsRecommendations;

type CanAccessCampaignsInsights = (state: YammerState) => boolean;
export const canAccessCampaignsInsights: CanAccessCampaignsInsights = (state) => canCreateCampaigns(state);

type IsCampaignLandingPageInaccessible = (state: YammerState, campaignId: Campaign['id']) => boolean;
export const isCampaignLandingPageInaccessible: IsCampaignLandingPageInaccessible = (state, campaignId) => {
  const canAccessCampaignLandingPage = canAccessCampaignsRecommendations(state);

  if (!canAccessCampaignLandingPage) {
    return true;
  }

  const campaign = findCampaignById(state, campaignId);
  if (!campaign) {
    return false;
  }

  const campaignReference = findCampaignReferenceByIdThrows(state, campaignId);

  return campaignReference.state === CampaignState.DRAFT && !campaign.viewerCanEditCampaign;
};

type CanEditCampaign = (state: YammerState, campaignId: Campaign['id']) => boolean;
export const canEditCampaign: CanEditCampaign = (state, campaignId) => {
  const canAccessDashboard = canAccessCampaignsDashboard(state);
  const canAccessCampaignLandingPage = canAccessCampaignsRecommendations(state);
  const campaign = findCampaignById(state, campaignId);

  return !!campaign?.viewerCanEditCampaign && canAccessDashboard && canAccessCampaignLandingPage;
};

type CanDeleteCampaign = (state: YammerState, campaignId: Campaign['id']) => boolean;
export const canDeleteCampaign: CanDeleteCampaign = (state, campaignId) => {
  const canAccessDashboard = canAccessCampaignsDashboard(state);
  const campaign = findCampaignById(state, campaignId);

  return !!campaign?.viewerCanDeleteCampaign && canAccessDashboard;
};

type CanPublishCampaign = (state: YammerState, campaignId: Campaign['id']) => boolean;
export const canPublishCampaign: CanPublishCampaign = (state, campaignId) => {
  const canEdit = canEditCampaign(state, campaignId);
  const campaignReference = findCampaignReferenceByIdThrows(state, campaignId);

  return canEdit && campaignReference.state !== CampaignState.ACTIVE;
};

type CanEndCampaign = (state: YammerState, campaignId: Campaign['id']) => boolean;
export const canEndCampaign: CanEndCampaign = (state, campaignId) => {
  const canEdit = canEditCampaign(state, campaignId);
  const campaignReference = findCampaignReferenceByIdThrows(state, campaignId);

  return canEdit && campaignReference.state === CampaignState.ACTIVE;
};
